<!-- Copyright: Seán I. O'Donoghue -->
<!-- TODO: styling for hover on dropdowns -->

<template>
  <nav>
    <h3>
      Videos
      <span v-if="$route.params.vimeoHash" class="count"> [1/{{ totalVideos }}] </span>
      <span v-else-if="totalVideos && filteredVideos" class="count">
        [{{ filteredVideos }}/{{ totalVideos }}]
      </span>
      <span v-else-if="totalVideos" class="count"> [{{ totalVideos }}] </span>
    </h3>
    <div id="right" :class="{reverse: !showYear}">
      <Dropdown
        v-if="showYear"
        placeholder="Year"
        :tag="$route.params.vimeoHash ? false : true"
        :options="years"
        :selected="year"
        path="/Videos" />
      <Dropdown
        v-if="showSession"
        placeholder="Session"
        :tag="$route.params.vimeoHash ? false : true"
        :options="sessions"
        :selected="session"
        path="/Videos" />
      <ButtonBespoke v-if="year || session" id="showAll" @click="showAll">Show All</ButtonBespoke>
    </div>
  </nav>
  <div class="view-wrapper">
    <VideoAccessInfoButton
      v-if="!$route.params.vimeoHash && (isUnlockActive || !hasFullVideoAccess)"
      :label="isUnlockActive ? 'Return to VIZBI Video Library' : 'How do I access all videos?'"
      @click="isUnlockActive ? $router.push('/Videos') : $router.push('/Videos/Unlock')" />
    <VideoLegend
      v-if="!$route.params.vimeoHash && !isUnlockActive"
      :showcases="Object.keys(showcases)" />
    <hr v-if="!$route.params.vimeoHash && !isUnlockActive" class="legend-rule" />
    <div class="router-view-wrapper">
      <router-view v-slot="{Component}">
        <component
          :is="Component"
          :options="options"
          @filterByYear="filterByYear"
          @filterBySession="filterBySession" />
      </router-view>
    </div>
  </div>
</template>

<script>
import {getVideoAccessNow} from "@/composables/videoAccessNow";
import {inject, ref, onMounted} from "vue";
import ButtonBespoke from "@/components/ButtonBespoke";
import Dropdown from "@/components/DropdownRoutes/Dropdown";
import VideoLegend from "@/components/Video/Legend";
import VideoAccessInfoButton from "@/components/Video/VideoAccessInfoButton.vue";
import useUserSafe from "@/composables/useUserSafe";
const reactiveDisplayWidth = ref(null);
export default {
  components: {
    ButtonBespoke,
    Dropdown,
    VideoLegend,
    VideoAccessInfoButton
  },
  setup() {
    const displayWidth = inject("displayWidth");
    const {user} = useUserSafe();
    onMounted(() => {
      reactiveDisplayWidth.value = displayWidth.value;
      console.log(`displayWidth = ${reactiveDisplayWidth.value}`);
    });
    return {reactiveDisplayWidth, user};
  },
  data() {
    return {
      got_showcase: {}, // remember video showcase
      got_privacy: {}, // remember video privacy
      thumbnails: {},
      registered_events: {},
      showcases: {}, // loaded from API in mounted()
      showYear: true,
      showSession: true,
      session: this.$route.params.session || "", // empty string = all session
      year: this.$route.params.year || "" // .toString() - empty string = all years
    };
  },
  computed: {
    isUnlockActive() {
      return this.$route.name === "Videos_Unlock";
    },
    hasFullVideoAccess() {
      const videoAccess = getVideoAccessNow(this.user?.publicMetadata?.videoAccess);
      const currentYear = new Date().getFullYear();
      return (videoAccess.workshopVideosUntil || 0) >= currentYear &&
        (videoAccess.masterclassVideosUntil || 0) >= currentYear;
    },
    options() {
      return {
        get_showcase: this.get_showcase,
        get_privacy: this.get_privacy,
        showcases: this.showcases,
        thumbnails: this.thumbnails,
        filteredShowcases: this.filteredShowcases,
        year: this.year,
        session: this.session
      };
    },
    dev_server() {
      if (window.location.host.match(/^dev.vizbi.org/)) {
        // on dev.vizbi.org, show staged videos
        return true;
      } else {
        return false;
      }
    },
    localhost() {
      if (window.location.host.match(/^localhost|^192/)) {
        // on localhost, show the very latest Showcase
        return true;
      } else {
        return false;
      }
    },
    years() {
      let years = [];
      Object.keys(this.showcases).forEach(function (year) {
        year = year.split(" ")[1];
        if (!years.includes(year)) years.push(year);
      });
      return years.sort((a, b) => b - a);
    },
    sessions() {
      let sessions = []; // 'All'
      var _this = this;
      Object.keys(this.showcases).forEach(function (year) {
        // Accumulate list of sessions
        Object.keys(_this.showcases[year].videos).forEach(function (hash) {
          let session = _this.showcases[year].videos[hash].tag;
          if (session && !sessions.includes(session) && !/\s/.test(session)) {
            sessions.push(session);
          }
        });
      });
      return sessions;
    },
    totalVideos() {
      return this.countVideosIn(this.showcases);
    },
    filteredShowcases() {
      if (this.$route.params.vimeoHash) return {};
      this.log(`filterShowcases(${this.session}, ${this.year})`);
      var _this = this;
      var filteredShowcases = {};
      var value;
      if (this.session) {
        // Retrieve list of session tags for the dropdown menu
        Object.keys(_this.showcases).forEach(function (year) {
          Object.keys(_this.showcases[year].videos).forEach(function (hash) {
            value = _this.showcases[year].videos[hash].tag;
            if (value === _this.session) {
              if (!filteredShowcases[year]) {
                filteredShowcases[year] = {};
                filteredShowcases[year].videos = {};
              }
              filteredShowcases[year].videos[hash] = _this.showcases[year].videos[hash];
            }
          });
        });
      } else if (this.year) {
        Object.keys(_this.showcases).forEach(function (item) {
          value = item.split(" ")[1];
          if (value === _this.year) {
            filteredShowcases[item] = _this.showcases[item];
          }
        });
      } else {
        filteredShowcases = this.showcases;
      }
      this.log(`filteredShowcases = ${Object.keys(filteredShowcases).length}`);
      return filteredShowcases;
    },
    filteredVideos() {
      let result = this.countVideosIn(this.filteredShowcases);
      if (result !== this.totalVideos) {
        return result;
      } else {
        return 0; // computed can't return 'undefined' -
      }
    }
  },
  async mounted() {
    this.log("Videos mounted()");
    this.showcases = await fetch("/sitemaps/videos/showcases-lite.json")
      .then((r) => r.ok ? r.json() : {}).catch(() => ({}));
    if (this.$route.params.vimeoHash) {
      await this.get_showcase(this.$route.params.vimeoHash); // don't need?
      this.log(`reactiveDisplayWidth.value = ${reactiveDisplayWidth.value}`);
      if (reactiveDisplayWidth.value === "narrow") {
        this.log(`displayWidth is narrow`);
        [this.showYear, this.showSession] = [false, false];
      }
      return;
    }
    if (this.$route.params.year) {
      [this.showYear, this.showSession] = [true, false];
      return;
    }
    if (!this.$route.params.session) return;
    if (!this.sessions.includes(this.$route.params.session)) {
      this.log(`URL has invalid session name`);
      this.showAll();
      return;
    }
    [this.showYear, this.showSession] = [false, true];
  },
  methods: {
    // eslint-disable-next-line
    log(message) {
      process.env.VUE_APP_DEV && console.log(message); // eslint-disable-line
    },
    get_privacy(vimeoHash, showcase) {
      if (this.got_privacy[vimeoHash]) {
        return this.got_privacy[vimeoHash]; // if privacy is already known
      }
      if (!showcase) {
        // if called without defining showcase, look it up
        showcase = this.get_showcase(vimeoHash);
      }
      let privacy;
      if (!showcase || !this.showcases[showcase]) {
        privacy = false; // unlisted video, so privacy is unknown
      } else if (!this.showcases[showcase]["videos"][vimeoHash]) {
        privacy = false; // privacy is unknown
      } else if (!this.showcases[showcase]["videos"][vimeoHash]["privacy"]) {
        privacy = false; // privacy is unknown
      } else {
        privacy = this.showcases[showcase]["videos"][vimeoHash]["privacy"] !== "anybody";
      }
      this.got_privacy[vimeoHash] = privacy; // remember privacy of this video
      return privacy;
    },
    filterByYear(year) {
      this.log(`filterByYear: '${year}'  (called from Tag)`);
      if (year === "All" || year === "") return this.showAll();
      this.$router.push(`/Videos/${year}`);
      // if (!this.$route.params.vimeoHash) {
      //   this.year = year;
      //   [this.showYear, this.showSession] = [true, false];
      //   this.session = "";
      // }
    },
    filterBySession(session) {
      this.log(`filterBySession: '${session}' (called from Tag)`);
      if (session === "All" || session === "") return this.showAll();
      this.$router.push(`/Videos/${session}`);
      // this.session = session;
      // [this.showYear, this.showSession] = [false, true];
      // this.year = "";
    },
    showAll() {
      this.log(`Show all years and sessions`);
      this.$router.push(`/Videos`);
      // this.year = "";
      // this.session = "";
      // [this.showYear, this.showSession] = [true, true];
      // if (this.$route.params.vimeoHash) return this.$router.push(`/Videos`);
      // history.pushState({}, null, `/Videos`); // silent
    },
    get_showcase(vimeoHash) {
      this.log(`get_showcase(${vimeoHash})`);
      let showcase; // local variable
      if (!vimeoHash) {
        console.warn(`vimeoHash not found`);
        return;
      }
      if (!this.showcases) {
        console.warn(`this.showcases not found`);
        return;
      }
      if (!Object.keys(this.showcases)) {
        console.warn(`No keys in this.showcases`);
        return;
      }
      if (Object.keys(this.showcases).length === 0) {
        console.warn(`No keys in this.showcases`);
        return;
      }
      if (this.got_showcase[vimeoHash]) {
        // if already found, use previous value
        return this.got_showcase[vimeoHash];
      }
      this.log(`About to check ${Object.keys(this.showcases).length} filteredShowcases`);
      for (showcase in this.showcases) {
        this.log(`Checking showcase: ${showcase}`);
        if (this.showcases[showcase]["videos"][vimeoHash]) {
          this.log(`Found showcase = ${showcase}`);
          let match = / (\d+)$/.exec(showcase);
          if (match[1]) {
            this.year = match[1];
            this.log(`Set year: "${this.year}"`);
          }
          this.session = this.showcases[showcase]["videos"][vimeoHash]["tag"];
          this.log(`Set session: "${this.session}"`);
          this.thumbnails = this.showcases[showcase]["videos"][vimeoHash]["pictures"];
          break; // end for loop
        } // otherwise, keep iterating over filteredShowcases
      }
      //return this.showcase;
      if (!showcase) {
        console.warn(`Missing needed meta data; returning to /Videos page`);
        showcase = ""; // for unlisted videos
        history.pushState({}, null, `/Videos`); // silent
      }
      this.got_showcase[vimeoHash] = showcase; // remember video showcase
      return;
    },
    countVideosIn(showcases) {
      // count total number of videos in a group of showcases
      let result = 0;
      for (let showcase of Object.values(showcases)) {
        // this.log(`result = ${result}`);
        // this.log(`showcase = ${Object.keys(showcase.videos).length}`);
        result += Object.keys(showcase.videos || {}).length;
        // this.log(`result = ${result}`);
      }
      return result;
    }
  }
};
</script>

<style scoped>
nav {
  display: flex;
  /* align-items: flex-start; */
  align-items: center;
}
h3 {
  display: inline;
  margin-bottom: 4px;
}
h3 span {
  font-weight: 100;
  font-size: 1rem;
  /* vertical-align: middle; */
}
.medium .count,
.narrow .count {
  display: none; /* mobile first */
}
#right {
  display: flex;
  align-items: flex-start; /* prevent from changing height*/
  gap: 0.8rem;
  /* margin-top: 0.2rem; */
  margin-left: auto;
}
#right.reverse {
  direction: rtl;
}
/* .narrow #showAll {
display: none;
} */
a {
  margin-right: 20px;
}
.view-wrapper {
  position: relative;
  margin-top: 8px;
}
.router-view-wrapper {
  margin-top: 0;
}
.legend-rule {
  border: none;
  border-top: 1px solid #2e2e2e;
  margin: 4px 0 6px;
}
</style>
